import sapi, {
  DeviceType,
  RoomsRequestParameters,
  RoomsResults,
  SapiClient,
  ProfileKey
} from '@findhotel/sapi'

interface SapiProps {
  anonymousId: string
  profileKey: ProfileKey
  currency?: string
  countryCode?: string
  language?: string
  deviceType: DeviceType
  pageSize?: number
  variations?: Record<string, string>
}

let client: SapiClient

/**
 * Initialises Sapi client so its ready to use Sapi SDK
 * @param anonymousId - User's anonymousId from the URL.
 * @param profileKey - App's profile key to initialise Sapi client with.
 * @param currency - User's currency from the URL.
 * @param countryCode - User's countryCode from the URL.
 * @param language - User's language from the URL.
 * @param deviceType - User's deviceType from the URL.
 * @param pageSize - Results page size.
 * @param variations - Experiment variations.
 */
export const initSapi = async ({
  anonymousId,
  profileKey,
  currency,
  countryCode,
  language,
  deviceType,
  pageSize = 0,
  variations = {}
}: SapiProps) => {
  if (!client) {
    // eslint-disable-next-line fp/no-mutation
    client = await sapi(profileKey, {
      anonymousId,
      pageSize,
      language,
      currency,
      countryCode,
      deviceType,
      variations
    })
  }
}

type HotelId = string
export interface GetRoomsProps {
  hotelId: string
  checkIn: string
  checkOut: string
  rooms: string
  cugDeals: string
  tier: string
  providerCode?: string
  oid?: string
}

/**
 * Sends request to get Rooms and Offers information
 * @param hotelId - The hotel's id.
 * @param checkIn - Selected check-in date.
 * @param checkOut - Selected check-out date.
 * @param rooms - Selected room configuration.
 * @param cugDeals - Closed User Group (CUG) information.
 * @param tier - User's tier ('member', 'plus' or '').
 * @param providerCode - Provider code.
 * @returns A promise that when resolved returns the rooms and offers information
 */
export const getRooms = async ({
  hotelId,
  checkIn,
  checkOut,
  rooms,
  cugDeals,
  tier,
  providerCode,
  oid
}: GetRoomsProps): Promise<{data: RoomsResults}> => {
  if (!client) return

  const roomsRequestBody: RoomsRequestParameters = {
    hotelId,
    checkIn,
    checkOut,
    rooms,
    cugDeals,
    tier,
    clickedOfferId: oid,
    providerCode
  }

  const response = await client.rooms(roomsRequestBody)

  return {
    data: response
  }
}

/**
 * Sends request to get Hotel static information
 * @param id - The hotel's id.
 * @returns A promise that when resolved returns the hotel's static content
 */
export const getHotel = async (id: HotelId) => {
  if (!client) return

  return client.hotel(id)
}
